import { CalendarIcon, CrownIcon, ShieldCheckIcon } from 'lucide-react';

import React from 'react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { Avatar, Badge } from '@splitfiesta/ui';

import { SupporterBadge } from '@/components/pro/SupporterBadge';

import { resolveUserName } from '@/lib/displayName';

export interface MemberData {
  id: string;
  name: string;
  profileImage?: string;
  joinedAt: string;
  /** Already gated by the `pro` flag in the container. */
  isSupporter?: boolean;
}

export interface MemberInfoProps {
  member: MemberData;
  isCurrentUser?: boolean;
  isOwner?: boolean;
  isAdmin?: boolean;
  showJoinDate?: boolean;
  showOwnerBadge?: boolean;
  showAdminBadge?: boolean;
  /** Rendered right after the name — the "Not joined yet" chip. */
  badge?: React.ReactNode;
  /** Extra line under the name, inside the row (e.g. the claim line). */
  secondary?: React.ReactNode;
  className?: string;
}

/**
 * Reusable component for consistent member information display
 * Shows avatar, name, join date, and owner badge
 */
function MemberInfoComponent({
  member,
  isCurrentUser = false,
  isOwner = false,
  isAdmin = false,
  showJoinDate = true,
  showOwnerBadge = true,
  showAdminBadge = true,
  badge,
  secondary,
  className = '',
}: MemberInfoProps) {
  const { t, i18n } = useTranslation();

  // Load required namespaces
  useNamespace('members');
  useNamespace('common');

  const formatMemberDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div
      className={`flex items-center min-w-0 ${className}`}
      data-testid={`member-info-${member.id}`}
    >
      <Avatar
        name={member.name}
        src={member.profileImage}
        size="md"
        className="mr-3 shrink-0"
        data-testid={`member-avatar-${member.id}`}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
          <p
            className="flex min-w-0 items-center text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100"
            data-testid={`member-name-${member.id}`}
          >
            <span className="min-w-0 truncate">
              {resolveUserName(member.name, t)}
              {isCurrentUser && (
                <span
                  className="text-gray-500 dark:text-gray-400 font-normal"
                  data-testid={`member-current-user-${member.id}`}
                >
                  {` (${t('common:labels.you')})`}
                </span>
              )}
            </span>
            {member.isSupporter && <SupporterBadge className="ml-1 shrink-0" />}
          </p>
          {badge}
          {isOwner && showOwnerBadge && (
            <Badge
              variant="warning"
              size="sm"
              className="shrink-0 w-fit"
              data-testid={`member-owner-badge-${member.id}`}
            >
              <CrownIcon className="w-3 h-3 mr-1" />
              {t('members:roles.owner')}
            </Badge>
          )}
          {isAdmin && !isOwner && showAdminBadge && (
            <Badge
              variant="info"
              size="sm"
              className="shrink-0 w-fit"
              data-testid={`member-admin-badge-${member.id}`}
            >
              <ShieldCheckIcon className="w-3 h-3 mr-1" />
              {t('members:roles.admin')}
            </Badge>
          )}
        </div>
        {showJoinDate && (
          <div className="flex flex-col sm:flex-row sm:items-center text-xs text-gray-500 dark:text-gray-400 mt-1 space-y-1 sm:space-y-0">
            <div
              className="flex items-center"
              data-testid={`member-join-date-${member.id}`}
            >
              <CalendarIcon className="w-3 h-3 mr-1 shrink-0 text-gray-400 dark:text-gray-500" />
              <span className="whitespace-nowrap">
                {t('members:status.joined', {
                  date: formatMemberDate(member.joinedAt),
                })}
              </span>
            </div>
          </div>
        )}
        {secondary && <div className="mt-1">{secondary}</div>}
      </div>
    </div>
  );
}

export interface MemberListItemProps {
  member: MemberData;
  isCurrentUser?: boolean;
  isOwner?: boolean;
  isAdmin?: boolean;
  showJoinDate?: boolean;
  showOwnerBadge?: boolean;
  showAdminBadge?: boolean;
  /** Rendered right after the name — the "Not joined yet" chip. */
  badge?: React.ReactNode;
  /** Extra line under the name, inside the row (e.g. the claim line). */
  secondary?: React.ReactNode;
  rightContent?: React.ReactNode;
  className?: string;
}

/**
 * Full member list item with container styling and optional right content
 * Uses MemberInfo for consistent member display
 */
function MemberListItemComponent({
  member,
  isCurrentUser = false,
  isOwner = false,
  isAdmin = false,
  showJoinDate = true,
  showOwnerBadge = true,
  showAdminBadge = true,
  badge,
  secondary,
  rightContent,
  className = '',
}: MemberListItemProps) {
  return (
    <div
      className={`flex items-center justify-between py-3 sm:py-4 hover:bg-[#F6F5F3] dark:hover:bg-gray-700/50 transition-colors ${className}`}
      data-testid={`member-item-${member.id}`}
    >
      <MemberInfo
        member={member}
        isCurrentUser={isCurrentUser}
        isOwner={isOwner}
        isAdmin={isAdmin}
        showJoinDate={showJoinDate}
        showOwnerBadge={showOwnerBadge}
        showAdminBadge={showAdminBadge}
        badge={badge}
        secondary={secondary}
      />
      {rightContent && (
        <div
          className="text-right ml-2 sm:ml-4 shrink-0"
          data-testid={`member-item-${member.id}-actions`}
        >
          {rightContent}
        </div>
      )}
    </div>
  );
}

// Memoize MemberInfo to prevent unnecessary re-renders
export const MemberInfo = React.memo(
  MemberInfoComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.member.id === nextProps.member.id &&
      prevProps.member.name === nextProps.member.name &&
      prevProps.member.profileImage === nextProps.member.profileImage &&
      prevProps.member.joinedAt === nextProps.member.joinedAt &&
      prevProps.member.isSupporter === nextProps.member.isSupporter &&
      prevProps.isCurrentUser === nextProps.isCurrentUser &&
      prevProps.isOwner === nextProps.isOwner &&
      prevProps.isAdmin === nextProps.isAdmin &&
      prevProps.showJoinDate === nextProps.showJoinDate &&
      prevProps.showOwnerBadge === nextProps.showOwnerBadge &&
      prevProps.showAdminBadge === nextProps.showAdminBadge &&
      prevProps.badge === nextProps.badge &&
      prevProps.secondary === nextProps.secondary &&
      prevProps.className === nextProps.className
    );
  }
);

MemberInfo.displayName = 'MemberInfo';

// Memoize MemberListItem to prevent unnecessary re-renders
export const MemberListItem = React.memo(
  MemberListItemComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.member.id === nextProps.member.id &&
      prevProps.member.name === nextProps.member.name &&
      prevProps.member.profileImage === nextProps.member.profileImage &&
      prevProps.member.joinedAt === nextProps.member.joinedAt &&
      prevProps.member.isSupporter === nextProps.member.isSupporter &&
      prevProps.isCurrentUser === nextProps.isCurrentUser &&
      prevProps.isOwner === nextProps.isOwner &&
      prevProps.isAdmin === nextProps.isAdmin &&
      prevProps.showJoinDate === nextProps.showJoinDate &&
      prevProps.showOwnerBadge === nextProps.showOwnerBadge &&
      prevProps.showAdminBadge === nextProps.showAdminBadge &&
      prevProps.badge === nextProps.badge &&
      prevProps.secondary === nextProps.secondary &&
      prevProps.className === nextProps.className &&
      prevProps.rightContent === nextProps.rightContent
    );
  }
);

MemberListItem.displayName = 'MemberListItem';

export default MemberListItem;
